import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { DataError, LoadingIndicator, Table } from "@digital-services/ui";
import { Pagination } from "@ds-cps/ui";

import { AzureApplicationUserModel } from "@api-users/part-1-api-user/models/api-users-models";
import { sortApiUsers } from "@api-users/part-1-api-user/sort-api-users";

import { useSortQueryString } from "@hooks/useSortQueryString";

import { useGetApiUsers } from "../../services/use-get-api-users";
import { CreateApiUserButton } from "../CreateApiUserButton";
import { NoApiUsersConfigured } from "../NoApiUsersConfigured";
import { ApiUsersDataRow } from "./ApiUsersDataRow";
import { ApiUsersHeaderRow } from "./ApiUsersHeaderRow";

// TBD - research re-rendering (<Root>-related)
export const ApiUsersTable: FunctionComponent = () => {
  const { t: tApiUsers } = useTranslation(undefined, {
    keyPrefix: "api-users"
  });

  const [apiUsersInMemory, setApiUsersInMemory] = useState<
    AzureApplicationUserModel[] | undefined
  >(undefined);

  const { activeSort, sortDirection, handleSort } = useSortQueryString({
    defaultSort: "name",
    defaultDirection: "ascending"
  });

  const { apiUsers, isLoading, error } = useGetApiUsers();

  useEffect(() => {
    const sortedUsers = sortApiUsers(apiUsers, activeSort, sortDirection);
    setApiUsersInMemory(sortedUsers);
  }, [apiUsers, activeSort, sortDirection]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center text-base">
        <LoadingIndicator label={tApiUsers("messages.users-loading")} />
      </div>
    );
  }

  if (error) {
    return (
      <DataError
        title={tApiUsers("messages.users-error")}
        message=""
        className="text-left! text-red-600"
      />
    );
  }

  if (!apiUsers?.length) {
    return <NoApiUsersConfigured />;
  }

  return (
    <>
      <div className="mb-3 flex items-center justify-end gap-3">
        <div className="grow"></div>
        <CreateApiUserButton />
      </div>

      <div data-testid="api-users-table">
        <Table className="w-full" rowHoverHighlight>
          <thead>
            <ApiUsersHeaderRow
              activeSort={activeSort}
              sortDirection={sortDirection}
              handleSort={handleSort}
            />
          </thead>

          <tbody data-testid="tbody">
            {apiUsersInMemory?.length
              ? apiUsersInMemory.map(apiUser => (
                  <ApiUsersDataRow
                    key={`api-user-${apiUser.id}`}
                    apiUser={apiUser}
                  />
                ))
              : null}
          </tbody>
        </Table>

        <Pagination totalCount={apiUsersInMemory?.length} />
      </div>
    </>
  );
};
